/**
 * Copyright 2020 (c) Dominik Basner, Sotec GmbH (for VDW e.V.)
 * Copyright 2020 (c) Fabian Wilhelm, Sotec GmbH (for VDW e.V.)
 * Copyright 2020-2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import React, { Component } from 'react';
import MachineListElement from '../components/machineList/machineListElement';
import { connect } from 'react-redux';
import { getFair, getMachineListFilter, getMachinesByFairAndParent } from '../redux/selectors';
import FilterModal, { IFilterDescription } from '../components/filterModal/filterModal';
import { setMachineListFilter, setMachineListFilterType } from '../redux/actions';
import FairMap from '../components/fairMap/fairMap';
import { labels } from '@umati/dashboard-common';
import { IFilterMachineData, MachinesFilter } from 'utilities/MachinesFilter';
import { IMachine, IFair } from '@umati/dashboard-types';
import { initialMachineFilterState } from 'redux/reducers/machineListFilter';
import { ISpecification, Specifications } from 'Specifications';
import { MachineryMachinesId } from 'consts/WellKnownIds';

interface IMachineListPropsEx {
  machines: IMachine[];
  fair?: IFair
  filter: IFilterMachineData,
}

interface IMachineListActions {
  setMachineListFilter: setMachineListFilterType;
}

interface IMachineListPropsAll extends IMachineListPropsEx, IMachineListActions {
  hidden?: boolean
}

interface IMachineListState {
  machines: IMachine[];
  expanded: Set<string>;
  showFilterWindow: boolean;
  filter: IFilterMachineData;
}

class MachineList extends Component<IMachineListPropsAll, IMachineListState> {
  constructor (props) {
    super(props);
    const machines = this.props.machines;
    const expanded = new Set<string>();

    this.handleExpand.bind(this);
    this.filterCallback.bind(this);
    this.mapCallback.bind(this);

    this.state = {
      filter: this.props.filter,
      machines,
      expanded,
      showFilterWindow: false
    };
  }

  componentDidMount () {
    const { hash } = window.location;
    if (hash !== '') {
      let retries = 0;
      const id = hash.replace('#', '');
      const scroll = () => {
        const element = document.getElementById(id);
        if (retries === 0) {
          setTimeout(scroll, 1000);
        } else if (retries >= 200) {
          return;
        } else if (element) {
          setTimeout(() => element.scrollIntoView(), 0);
        } else {
          setTimeout(scroll, 100);
        }
        retries = retries + 1;
      };
      scroll();
    }
  }

  isExpanded = (machine: IMachine) => {
    return this.state.expanded.has(machine.MachineId);
  };

  static getDerivedStateFromProps = (newProps: IMachineListPropsAll, prevState: IMachineListState) => {
    const newState = { ...prevState, machines: newProps.machines };
    const dontUpdateExpanded = (prevState.machines.length === newProps.machines.length);
    return MachineList.filter(newState.filter, newState, newProps.fair, dontUpdateExpanded);
  };

  static expand = (key, expSet) => {
    if (expSet.has(key)) {
      expSet.delete(key);
    } else {
      expSet.add(key);
    }
    return expSet;
  };

  handleExpand = (key) => {
    const expSet = MachineList.expand(key, this.state.expanded);
    this.setState({ ...this.state, expanded: expSet });
  };

  mapCallback = (data) => {
    this.setState({ ...this.state, filter: { ...this.state.filter, halls: data } });
  };

  static compareMachines (machineA: IMachine, machineB: IMachine) {
    if (!machineA && !machineB) {
      return 0;
    } else if (!machineA) {
      return -1;
    } else if (!machineB) {
      return 1;
    }

    if (!machineA.Data?.Model?.text && !machineB.Data?.Model?.text) {
      return 0;
    } else if (!machineA.Data?.Model?.text) {
      return -1;
    } else if (!machineB.Data?.Model?.text) {
      return 1;
    }

    const nameA = machineA.Data.Model.text.toLowerCase();
    const nameB = machineB.Data.Model.text.toLowerCase();
    return nameA.localeCompare(nameB, 'en');
  }

  static filter = (filterData, state: IMachineListState, fair?: IFair, ignoreDetails = false) => {
    state.filter = filterData;
    if (!ignoreDetails) {
      if (filterData.showDetails) {
        state.expanded = new Set(state.machines.map(m => m.MachineId));
      } else {
        state.expanded = new Set();
      }
    }
    if (fair !== undefined) {
      state.filter.fairTag = fair.Tag;
    }
    return state;
  };

  static isExpanded = (machine: IMachine, expds) => {
    return expds.has(machine.MachineId);
  };

  filterCallback = (filterData) => {
    const state = MachineList.filter(filterData, this.state, this.props.fair);
    this.props.setMachineListFilter(state.filter);
    this.setState(state);
  };

  render () {
    const defaults = initialMachineFilterState;
    const fairTag = this.props.fair?.Tag;
    const filter: IFilterDescription = {
      checkboxes: [{
        cbKey: 'showDetails',
        label: 'State',
        value: this.state.filter.showDetails,
        default: defaults.showDetails,
        toggleButton: true
      },
      {
        cbKey: 'showFavourites',
        label: labels.favorites,
        value: this.state.filter.showFavourites,
        default: defaults.showFavourites,
        toggleButton: false
      },
      {
        cbKey: 'onlineOnly',
        label: labels.onlineOnly,
        value: this.state.filter.onlineOnly,
        default: defaults.onlineOnly,
        toggleButton: false
      }
      ],
      searchbars: [{
        cbKey: 'searchValue',
        label: labels.searchPlaceholder,
        value: this.state.filter.searchValue,
        default: defaults.searchValue
      }],
      dropdowns: [
        {
          cbKey: 'Specification',
          label: labels.specification,
          choices: ['all', ...Specifications.map(s => s.DisplayName)],
          value: this.state.filter.Specification,
          valueToChoice: (spec: ISpecification | 'all') => !spec || spec === 'all' ? spec : spec.DisplayName,
          choiceToValue: (value: string): ISpecification | string => Specifications.find(s => s.DisplayName === value) || 'all',
          default: defaults.Specification
        }
      ]
    };
    const fairHalls = this.props.fair?.Halls || [];
    if (fairTag !== 'all') {
      filter.dropdowns = filter.dropdowns?.concat([{
        cbKey: 'halls',
        label: labels.hall,
        choices: ['all', ...fairHalls],
        value: this.state.filter.halls,
        default: defaults.halls
      }
      ]);
      filter.toggles = filter.toggles = [{
        cbKey: 'show',
        choices: [{
          value: 'list',
          label: 'List',
          icon: 'btn-icon-list'
        }, {
          value: 'map',
          label: 'Map',
          icon: 'btn-icon-map'
        }],
        value: this.state.filter.show,
        default: defaults.show
      }];
    }

    const child = {
      type: 'dropdowns',
      idx: 1,
      cbKey: 'halls',
      isVisible: this.state.filter.show === 'map'
    };

    const machfilter = new MachinesFilter(this.state.filter);
    const machines = machfilter.filter(this.props.machines);
    return (
      <div className="container view-list">
        { !this.props.hidden && <FilterModal filter={filter} callback={this.filterCallback} child={child} >
          <FairMap fair={this.props.fair} locationHall={this.props.filter.halls} />
        </FilterModal> }
        <ul className="list-group">
          {machines.map(m =>
            <MachineListElement
              key={m.MachineId}
              machine={m}
              in={MachineList.isExpanded(m, this.state.expanded)}
              fairTag={fairTag || ''}
              cb={{ expand: () => this.handleExpand(m.MachineId) }} />
          )}
        </ul>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps): IMachineListPropsEx => {
  const fairTag = ownProps.match.params.fair as string;
  const machines = getMachinesByFairAndParent(state, fairTag, MachineryMachinesId);
  return {
    machines: machines || [],
    fair: getFair(state, fairTag),
    filter: getMachineListFilter(state, fairTag)
  };
};

export default connect(mapStateToProps, { setMachineListFilter })(MachineList);
